import { useState, useEffect, useRef } from "react";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  InputAdornment,
  Divider,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { toast } from "react-toastify";
import { getInitials, getAvatarColor } from "../../utils/avatarUtils";

export default function Profile() {
  const role = localStorage.getItem("role") || "Staff";

  // Profile state
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Password change dialog
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordStep, setPasswordStep] = useState(1);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Confirmation dialogs
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [confirmPhotoOpen, setConfirmPhotoOpen] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState(null);

  const [profileFormData, setProfileFormData] = useState({
    fullName: "",
    username: "",
    email: "",
    phoneNumber: "",
  });

  // Admin account creation state
  const [openDialog, setOpenDialog] = useState(false);
  const [accountType, setAccountType] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const inputRefs = useRef([]);

  const [formData, setFormData] = useState({
    fullName: "",
    username: "",
    email: "",
    monthlySalary: "",
    designation: "",
    staffType: "Task-Based",
    generatedPassword: "",
  });

  const steps = ["Enter Details", "Verify Email", "Account Created"];

  // Fetch user profile on mount
  useEffect(() => {
    fetchProfile();
  }, []);

  // OTP Timer countdown
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Profile management functions
  const fetchProfile = async () => {
    try {
      setProfileLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile);
        setProfileFormData({
          fullName: data.profile.fullName || "",
          username: data.profile.username || "",
          email: data.profile.email || "",
          phoneNumber: data.profile.phoneNumber || "",
        });
      } else {
        toast.error("Failed to fetch profile");
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to fetch profile");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileInputChange = (e) => {
    setProfileFormData({ ...profileFormData, [e.target.name]: e.target.value });
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    // Store file and show confirmation
    setPendingPhoto(file);
    setConfirmPhotoOpen(true);
  };

  const confirmPhotoUpload = async () => {
    if (!pendingPhoto) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append("photo", pendingPhoto);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/profile/photo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formDataUpload,
      });

      if (response.ok) {
        const data = await response.json();
        setProfile({ ...profile, photo: data.photoUrl });
        toast.success("Photo uploaded successfully");
        setConfirmPhotoOpen(false);
        setPendingPhoto(null);
        
        // Trigger navbar refresh by dispatching custom event
        window.dispatchEvent(new Event('profilePhotoUpdated'));
      } else {
        toast.error("Failed to upload photo");
      }
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = () => {
    if (!profileFormData.fullName || !profileFormData.username || !profileFormData.phoneNumber) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Validate email for admins
    if (role === "Admin" && profileFormData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(profileFormData.email)) {
        toast.error("Please enter a valid email address");
        return;
      }
    }
    
    setConfirmSaveOpen(true);
  };

  const confirmSaveProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      
      const updateData = {
        fullName: profileFormData.fullName,
        username: profileFormData.username,
        phoneNumber: profileFormData.phoneNumber,
      };
      
      // Only include email if admin is updating it
      if (role === "Admin") {
        updateData.email = profileFormData.email;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();

      if (response.ok) {
        setProfile(data.profile);
        setEditMode(false);
        localStorage.setItem("fullName", data.profile.fullName);
        localStorage.setItem("username", data.profile.username);
        toast.success("Profile updated successfully");
        setConfirmSaveOpen(false);
      } else {
        toast.error(data.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    if (profile) {
      setProfileFormData({
        fullName: profile.fullName || "",
        username: profile.username || "",
        email: profile.email || "",
        phoneNumber: profile.phoneNumber || "",
      });
    }
  };

  // Password change handlers
  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleVerifyCurrentPassword = async () => {
    if (!passwordData.currentPassword) {
      toast.error("Please enter your current password");
      return;
    }

    setVerifyingPassword(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/profile/verify-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Password verified! Now enter your new password");
        setPasswordStep(2);
      } else {
        toast.error(data.message || "Incorrect password");
      }
    } catch (error) {
      console.error("Error verifying password:", error);
      toast.error("Failed to verify password");
    } finally {
      setVerifyingPassword(false);
    }
  };

  const handleChangePassword = async () => {
    // Staff must provide current password, admin doesn't need to
    if (role === "Staff" && !passwordData.currentPassword) {
      toast.error("Please enter your current password");
      return;
    }

    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error("Please fill in new password fields");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      
      const updateData = {
        fullName: profile.fullName,
        username: profile.username,
        phoneNumber: profile.phoneNumber,
        newPassword: passwordData.newPassword,
      };
      
      // Only include current password if staff is changing it
      if (role === "Staff") {
        updateData.currentPassword = passwordData.currentPassword;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Password changed successfully");
        setPasswordDialogOpen(false);
        setPasswordStep(1);
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        toast.error(data.message || "Failed to change password");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error("Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  // Admin account creation handlers
  const handleOpenDialog = (type) => {
    setAccountType(type);
    setOpenDialog(true);
    setActiveStep(0);
    setOtpDigits(["", "", "", ""]);
    setFormData({
      fullName: "",
      username: "",
      email: "",
      monthlySalary: "",
      designation: "",
      staffType: "Task-Based",
      generatedPassword: "",
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setAccountType("");
    setActiveStep(0);
    setOtpTimer(0);
    setOtpDigits(["", "", "", ""]);
    setFormData({
      fullName: "",
      username: "",
      email: "",
      monthlySalary: "",
      designation: "",
      staffType: "Task-Based",
      generatedPassword: "",
    });
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/, "");
    const newOtp = [...otpDigits];
    newOtp[index] = value;
    setOtpDigits(newOtp);
    if (value && index < 3) inputRefs.current[index + 1].focus();
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSendOTP = async () => {
    if (!formData.fullName || !formData.username || !formData.email) {
      toast.error("Please fill in all fields");
      return;
    }

    if (accountType === "staff") {
      if (!formData.designation) {
        toast.error("Please enter staff designation");
        return;
      }
      if (!formData.monthlySalary || parseFloat(formData.monthlySalary) <= 0) {
        toast.error("Please enter a valid monthly salary");
        return;
      }
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/admin/create-account/send-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          email: formData.email,
          fullName: formData.fullName,
          username: formData.username,
          designation: accountType === "staff" ? formData.designation : undefined,
          staffType: accountType === "staff" ? formData.staffType : undefined,
          monthlySalary: accountType === "staff" ? parseFloat(formData.monthlySalary) : undefined,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("OTP sent to email!");
        setOtpTimer(600);
        setActiveStep(1);
      } else {
        toast.error(data.message || "Failed to send OTP");
      }
    } catch (error) {
      toast.error("Error sending OTP");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndCreate = async () => {
    const otpCode = otpDigits.join("");
    if (otpCode.length !== 4) {
      toast.error("Please enter the complete 4-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/admin/create-account/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          email: formData.email,
          fullName: formData.fullName,
          username: formData.username,
          otp: otpCode,
          role: accountType === "admin" ? "Admin" : "Staff",
          designation: accountType === "staff" ? formData.designation : undefined,
          staffType: accountType === "staff" ? formData.staffType : undefined,
          monthlySalary: accountType === "staff" ? parseFloat(formData.monthlySalary) : undefined,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setFormData({ ...formData, generatedPassword: data.password });
        setActiveStep(2);
        toast.success(`${accountType === "admin" ? "Admin" : "Staff"} account created successfully!`);
      } else {
        toast.error(data.message || "Failed to create account");
      }
    } catch (error) {
      toast.error("Error creating account");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fullName = profile?.fullName || localStorage.getItem("fullName") || "User";
  const initials = getInitials(fullName);
  const avatarColor = getAvatarColor(fullName);

  if (profileLoading) {
    return (
      <AppLayout>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", background: "linear-gradient(135deg,#1a1a1a 0%,#2d2d2d 100%)" }}>
          <CircularProgress sx={{ color: "#C4A574" }} />
        </Box>
      </AppLayout>
    );
  }

  if (!profile) {
    return (
      <AppLayout>
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "60vh", gap: 2 }}>
          <Typography variant="h6" sx={{ color: "#8B7355" }}>
            Failed to load profile
          </Typography>
          <Button
            variant="contained"
            onClick={fetchProfile}
            sx={{
              bgcolor: "#C4A574",
              color: "white",
              fontWeight: 600,
              "&:hover": { bgcolor: "#B39562" },
            }}
          >
            Retry
          </Button>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ minHeight: "calc(100vh - 80px)", p: { xs: 2, md: 4 } }}>
        <Box sx={{ maxWidth: 1400, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: "#C4A574", mb: 1 }}>
              My Profile
            </Typography>
            <Typography variant="body1" sx={{ color: "#bbb" }}>
              Manage your account settings and preferences
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* LEFT: 40% on md+ (md=5), 100% on small */}
            <Grid item xs={12} sx={{width: '100%'}}>
              <Card xs={12} sx={{ bgcolor: "#2a2a2a", color: "#fff", border: "1px solid rgba(196,165,116,0.08)", borderRadius: 2 }}>
                <Box  xs={12} sx={{ height: 120, background: "linear-gradient(135deg,#C4A574 0%,#8B7355 100%)", borderTopLeftRadius: 8, borderTopRightRadius: 8 }} />
                <CardContent sx={{ textAlign: "center", mt: -6 }} xs={12}>
                  <Box  xs={12} sx={{ position: "relative", display: "inline-block" }}>
                    {profile?.photo ? (
                      <Avatar src={`${API_BASE_URL}${profile.photo}`} sx={{ width: 120, height: 120, border: "4px solid #222", boxShadow: 3 }} />
                    ) : (
                      <Avatar sx={{ width: 120, height: 120, bgcolor: avatarColor, fontSize: "2.5rem", fontWeight: 600, border: "4px solid #222", boxShadow: 3 }}>
                        {initials}
                      </Avatar>
                    )}

                    <IconButton component="label" disabled={uploading} sx={{ position: "absolute", bottom: 0, right: 0, bgcolor: "#C4A574", color: "#000", "&:hover": { bgcolor: "#B39562" }, width: 40, height: 40 }}>
                      {uploading ? <CircularProgress size={20} sx={{ color: "#000" }} /> : <PhotoCameraIcon fontSize="small" />}
                      <input type="file" hidden accept="image/*" onChange={handlePhotoUpload} />
                    </IconButton>
                  </Box>

                  <Typography variant="h5" sx={{ mt: 2, fontWeight: 600, color: "#fff" }}>
                    {profile?.fullName}
                  </Typography>

                  <Chip label={profile?.role} sx={{ mt: 1, bgcolor: profile?.role === "Admin" ? "#8B7355" : "#C4A574", color: "#fff", fontWeight: 600 }} />

                  <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.06)" }} />

                  <Stack spacing={2} sx={{ textAlign: "left", px: { xs: 1, md: 3 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EmailIcon sx={{ color: "#C4A574", fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Email</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#fff" }}>{profile?.email}</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PhoneIcon sx={{ color: "#C4A574", fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Phone</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: "#fff" }}>{profile?.phoneNumber || "Not provided"}</Typography>
                      </Box>
                    </Box>

                    {profile?.designation && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <BadgeIcon sx={{ color: "#C4A574", fontSize: 20 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Designation</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: "#fff" }}>{profile.designation}</Typography>
                        </Box>
                      </Box>
                    )}

                    {profile?.joiningDate && role === "Staff" && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CalendarTodayIcon sx={{ color: "#C4A574", fontSize: 20 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Joined</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: "#fff" }}>
                            {new Date(profile.joiningDate).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {profile?.salary && role === "Staff" && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <AttachMoneyIcon sx={{ color: "#C4A574", fontSize: 20 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#bbb", display: "block" }}>Monthly Salary</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#4CAF50" }}>
                            ₹{profile.salary.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Stack>

                  <Button fullWidth variant="outlined" startIcon={<LockIcon />} onClick={() => setPasswordDialogOpen(true)} sx={{ mt: 3, borderColor: "#C4A574", color: "#C4A574", fontWeight: 600 }}>
                    Change Password
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* RIGHT: 60% on md+ (md=7), 100% on small */}
            <Grid item xs={12}>
              <Grid container spacing={3}>
                {/* First row: Personal Information */}
                <Grid item xs={12}  sx={{width: '100%'}}>
                  <Card xs={12} sx={{ bgcolor: "#2a2a2a", color: "#fff", border: "1px solid rgba(196,165,116,0.08)", borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "#C4A574", mb: 2 }}>Personal Information</Typography>
                      
                      <Box sx={{ display: "flex", mb: 3 }}>
                        {!editMode ? (
                          <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditMode(true)} sx={{ bgcolor: "#C4A574", color: "#000", fontWeight: 600 }}>
                            Edit Profile
                          </Button>
                        ) : (
                          <Stack direction="row" spacing={1}>
                            <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancelEdit} sx={{ borderColor: "#8B7355", color: "#8B7355", fontWeight: 600 }}>Cancel</Button>
                            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveProfile} disabled={saving} sx={{ bgcolor: "#C4A574", color: "#000", fontWeight: 600 }}>
                              {saving ? <CircularProgress size={20} color="inherit" /> : "Save"}
                            </Button>
                          </Stack>
                        )}
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Full Name" name="fullName" value={profileFormData.fullName} onChange={handleProfileInputChange} disabled={!editMode || role === "Staff"} InputProps={{ readOnly: !editMode || role === "Staff" }} helperText={role === "Staff" && !editMode ? "Contact admin to change" : ""} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Username" name="username" value={profileFormData.username} onChange={handleProfileInputChange} disabled={!editMode} helperText={editMode ? "You can change your username" : ""} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Email" name="email" value={role === "Admin" && editMode ? profileFormData.email : (profile?.email || "")} onChange={handleProfileInputChange} disabled={role === "Staff" || !editMode} helperText={role === "Staff" ? "Contact admin to change email" : (editMode && role === "Admin" ? "You can edit your email" : "")} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Phone Number" name="phoneNumber" value={profileFormData.phoneNumber} onChange={handleProfileInputChange} disabled={!editMode} />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Second row: Account Management */}
                {role === "Admin" && (
                  <Grid item xs={12}  sx={{width: '100%'}}>
                    <Card xs={12} sx={{ bgcolor: "#2a2a2a", color: "#fff", border: "1px solid rgba(196,165,116,0.08)", borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: "#C4A574", mb: 2 }}>Account Management</Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Button fullWidth variant="contained" startIcon={<PersonAddIcon />} onClick={() => handleOpenDialog("staff")} sx={{ bgcolor: "#C4A574", color: "#000", py: 1.5, fontWeight: 600 }}>
                              Create Staff Account
                            </Button>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Button fullWidth variant="contained" startIcon={<AdminPanelSettingsIcon />} onClick={() => handleOpenDialog("admin")} sx={{ bgcolor: "#8B7355", color: "#fff", py: 1.5, fontWeight: 600 }}>
                              Create Admin Account
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>

        {/* Password Change Dialog */}
        <Dialog open={passwordDialogOpen} onClose={() => { setPasswordDialogOpen(false); setPasswordStep(1); }} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: "#C4A574", color: "white", fontWeight: 700 }}>
            Change Password
          </DialogTitle>
          <DialogContent sx={{ pt: 3, bgcolor: "#2a2a2a", color: "#fff" }}>
            <Stack spacing={3}>
              {/* Staff: Step 1 - Verify Current Password */}
              {role === "Staff" && passwordStep === 1 && (
                <>
                  <Typography variant="body2" sx={{ color: "#bbb" }}>
                    Step 1: Please enter your current password to verify your identity
                  </Typography>
                  <TextField
                    fullWidth
                    label="Current Password"
                    name="currentPassword"
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                    autoFocus
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#1a1a1a',
                        color: '#fff',
                        '& fieldset': { borderColor: 'rgba(196,165,116,0.3)' },
                        '&:hover fieldset': { borderColor: '#C4A574' },
                        '&.Mui-focused fieldset': { borderColor: '#C4A574' }
                      },
                      '& .MuiInputLabel-root': { color: '#bbb' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#C4A574' }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end" sx={{ color: '#bbb' }}>
                            {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}

              {/* Staff: Step 2 - Enter New Password OR Admin: Direct Entry */}
              {((role === "Staff" && passwordStep === 2) || role === "Admin") && (
                <>
                  {role === "Admin" && (
                    <Typography variant="body2" sx={{ color: "#bbb", fontStyle: "italic" }}>
                      As an admin, you can change your password directly without verification.
                    </Typography>
                  )}
                  {role === "Staff" && (
                    <Typography variant="body2" sx={{ color: "#4CAF50", fontWeight: 600 }}>
                      ✓ Password verified! Step 2: Enter your new password
                    </Typography>
                  )}
                  <TextField
                    fullWidth
                    label="New Password"
                    name="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    helperText="Password must be at least 6 characters long"
                    autoFocus={role === "Admin"}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#1a1a1a',
                        color: '#fff',
                        '& fieldset': { borderColor: 'rgba(196,165,116,0.3)' },
                        '&:hover fieldset': { borderColor: '#C4A574' },
                        '&.Mui-focused fieldset': { borderColor: '#C4A574' }
                      },
                      '& .MuiInputLabel-root': { color: '#bbb' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#C4A574' },
                      '& .MuiFormHelperText-root': { color: '#bbb' }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" sx={{ color: '#bbb' }}>
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#1a1a1a',
                        color: '#fff',
                        '& fieldset': { borderColor: 'rgba(196,165,116,0.3)' },
                        '&:hover fieldset': { borderColor: '#C4A574' },
                        '&.Mui-focused fieldset': { borderColor: '#C4A574' }
                      },
                      '& .MuiInputLabel-root': { color: '#bbb' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#C4A574' }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" sx={{ color: '#bbb' }}>
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1, bgcolor: "#2a2a2a" }}>
            <Button
              onClick={() => {
                setPasswordDialogOpen(false);
                setPasswordStep(1);
                setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
              }}
              sx={{ color: "#8B7355", fontWeight: 600 }}
            >
              Cancel
            </Button>
            {role === "Staff" && passwordStep === 1 && (
              <Button
                onClick={handleVerifyCurrentPassword}
                variant="contained"
                disabled={verifyingPassword}
                sx={{
                  bgcolor: "#C4A574",
                  color: "#000",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#B39562" },
                }}
              >
                {verifyingPassword ? <CircularProgress size={24} sx={{ color: "#000" }} /> : "Verify Password"}
              </Button>
            )}
            {((role === "Staff" && passwordStep === 2) || role === "Admin") && (
              <Button
                onClick={handleChangePassword}
                variant="contained"
                disabled={saving}
                sx={{
                  bgcolor: "#C4A574",
                  color: "#000",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#B39562" },
                }}
              >
                {saving ? <CircularProgress size={24} sx={{ color: "#000" }} /> : "Change Password"}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Create Account Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: "#C4A574", color: "white", fontWeight: 700 }}>
            Create {accountType === "admin" ? "Admin" : "Staff"} Account
          </DialogTitle>
          <DialogContent sx={{ pt: 3, bgcolor: "#2a2a2a", color: "#fff" }}>
            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel sx={{ '& .MuiStepLabel-label': { color: '#bbb' } }}>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 && (
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#1a1a1a',
                      color: '#fff',
                      '& fieldset': { borderColor: 'rgba(196,165,116,0.3)' },
                      '&:hover fieldset': { borderColor: '#C4A574' },
                      '&.Mui-focused fieldset': { borderColor: '#C4A574' }
                    },
                    '& .MuiInputLabel-root': { color: '#bbb' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C4A574' }
                  }}
                />
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  helperText="User can login with this username"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#1a1a1a',
                      color: '#fff',
                      '& fieldset': { borderColor: 'rgba(196,165,116,0.3)' },
                      '&:hover fieldset': { borderColor: '#C4A574' },
                      '&.Mui-focused fieldset': { borderColor: '#C4A574' }
                    },
                    '& .MuiInputLabel-root': { color: '#bbb' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C4A574' },
                    '& .MuiFormHelperText-root': { color: '#bbb' }
                  }}
                />
                {accountType === "staff" && (
                  <>
                    <TextField
                      fullWidth
                      label="Designation"
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Tailor, Cutter, Designer"
                      helperText="Staff member's job designation"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#1a1a1a',
                          color: '#fff',
                          '& fieldset': { borderColor: 'rgba(196,165,116,0.3)' },
                          '&:hover fieldset': { borderColor: '#C4A574' },
                          '&.Mui-focused fieldset': { borderColor: '#C4A574' }
                        },
                        '& .MuiInputLabel-root': { color: '#bbb' },
                        '& .MuiInputLabel-root.Mui-focused': { color: '#C4A574' },
                        '& .MuiFormHelperText-root': { color: '#bbb' }
                      }}
                    />
                    <FormControl fullWidth sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#1a1a1a',
                        color: '#fff',
                        '& fieldset': { borderColor: 'rgba(196,165,116,0.3)' },
                        '&:hover fieldset': { borderColor: '#C4A574' },
                        '&.Mui-focused fieldset': { borderColor: '#C4A574' }
                      },
                      '& .MuiInputLabel-root': { color: '#bbb' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#C4A574' }
                    }}>
                      <InputLabel>Staff Type *</InputLabel>
                      <Select
                        name="staffType"
                        value={formData.staffType}
                        label="Staff Type *"
                        onChange={handleInputChange}
                        sx={{ color: '#fff' }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: '#1a1a1a',
                              '& .MuiMenuItem-root': {
                                color: '#fff',
                                '&:hover': { bgcolor: '#333' },
                                '&.Mui-selected': { bgcolor: '#C4A574', color: '#000' }
                              }
                            }
                          }
                        }}
                      >
                        <MenuItem value="Task-Based">Task-Based</MenuItem>
                        <MenuItem value="Hourly">Hourly</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="Monthly Salary (₹)"
                      name="monthlySalary"
                      type="text"
                      value={formData.monthlySalary}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
                          handleInputChange(e);
                        }
                      }}
                      required
                      placeholder="Enter monthly salary"
                      helperText="Staff member's monthly salary"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#1a1a1a',
                          color: '#fff',
                          '& fieldset': { borderColor: 'rgba(196,165,116,0.3)' },
                          '&:hover fieldset': { borderColor: '#C4A574' },
                          '&.Mui-focused fieldset': { borderColor: '#C4A574' }
                        },
                        '& .MuiInputLabel-root': { color: '#bbb' },
                        '& .MuiInputLabel-root.Mui-focused': { color: '#C4A574' },
                        '& .MuiFormHelperText-root': { color: '#bbb' }
                      }}
                    />
                  </>
                )}
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  helperText="User can also login with this email. Password will be sent here."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#1a1a1a',
                      color: '#fff',
                      '& fieldset': { borderColor: 'rgba(196,165,116,0.3)' },
                      '&:hover fieldset': { borderColor: '#C4A574' },
                      '&.Mui-focused fieldset': { borderColor: '#C4A574' }
                    },
                    '& .MuiInputLabel-root': { color: '#bbb' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C4A574' },
                    '& .MuiFormHelperText-root': { color: '#bbb' }
                  }}
                />
              </Stack>
            )}

            {activeStep === 1 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 2, textAlign: "center", color: "#bbb" }}>
                  Enter the 4-digit code sent to <strong>{formData.email}</strong>
                </Typography>

                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                  {otpDigits.map((digit, index) => (
                    <TextField
                      key={index}
                      inputRef={(el) => (inputRefs.current[index] = el)}
                      value={digit}
                      onChange={(e) => handleOtpChange(e, index)}
                      onKeyDown={(e) => handleOtpKeyDown(e, index)}
                      inputProps={{
                        maxLength: 1,
                        style: {
                          textAlign: "center",
                          fontSize: "1.2rem",
                          width: "2.3rem",
                          height: "2.6rem",
                          color: "#fff"
                        },
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#1a1a1a',
                          '& fieldset': { borderColor: 'rgba(196,165,116,0.3)' },
                          '&:hover fieldset': { borderColor: '#C4A574' },
                          '&.Mui-focused fieldset': { borderColor: '#C4A574' }
                        }
                      }}
                    />
                  ))}
                </Stack>

                {otpTimer > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "center",
                      mt: 1,
                      color: "#bbb",
                      fontSize: "0.75rem",
                    }}
                  >
                    Code expires in: {formatTime(otpTimer)}
                  </Typography>
                )}
                {otpTimer === 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "center",
                      mt: 1,
                      color: "error.main",
                      fontSize: "0.75rem",
                    }}
                  >
                    OTP has expired. Please close and request a new one.
                  </Typography>
                )}
              </Box>
            )}

            {activeStep === 2 && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2, color: "success.main", fontWeight: 600 }}>
                  ✓ Account created successfully!
                </Typography>
                <Paper sx={{ p: 2, bgcolor: "#1a1a1a", mb: 2, border: "1px solid rgba(196,165,116,0.2)" }}>
                  <Typography variant="body2" sx={{ mb: 1, color: "#fff" }}>
                    <strong>Full Name:</strong> {formData.fullName}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, color: "#fff" }}>
                    <strong>Username:</strong> {formData.username}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, color: "#fff" }}>
                    <strong>Email:</strong> {formData.email}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, color: "success.main" }}>
                    <strong>✓ Password sent to email</strong>
                  </Typography>
                </Paper>
                <Typography variant="caption" sx={{ display: "block", color: "#bbb", textAlign: "center" }}>
                  A random password has been sent to {formData.email}. The user can login with either their email or
                  username.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1, bgcolor: "#2a2a2a" }}>
            <Button onClick={handleCloseDialog} sx={{ color: "#C4A574", fontWeight: 600 }}>
              {activeStep === 2 ? "Close" : "Cancel"}
            </Button>
            {activeStep === 0 && (
              <Button
                onClick={handleSendOTP}
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: "#C4A574",
                  color: "#000",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#B39562" },
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: "#000" }} /> : "Send OTP"}
              </Button>
            )}
            {activeStep === 1 && (
              <Button
                onClick={handleVerifyAndCreate}
                variant="contained"
                disabled={loading || otpTimer === 0}
                sx={{
                  bgcolor: "#C4A574",
                  color: "#000",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#B39562" },
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: "#000" }} /> : "Verify & Create"}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Save Confirmation Dialog */}
        <Dialog open={confirmSaveOpen} onClose={() => setConfirmSaveOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: "#C4A574", color: "white", fontWeight: 700 }}>
            Confirm Profile Update
          </DialogTitle>
          <DialogContent sx={{ pt: 3, bgcolor: "#2a2a2a", color: "#fff" }}>
            <Typography>
              Are you sure you want to save these changes to your profile?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1, bgcolor: "#2a2a2a" }}>
            <Button
              onClick={() => setConfirmSaveOpen(false)}
              sx={{ color: "#8B7355", fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmSaveProfile}
              variant="contained"
              disabled={saving}
              sx={{
                bgcolor: "#C4A574",
                color: "#000",
                fontWeight: 600,
                "&:hover": { bgcolor: "#B39562" },
              }}
            >
              {saving ? <CircularProgress size={24} sx={{ color: "#000" }} /> : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Photo Upload Confirmation Dialog */}
        <Dialog open={confirmPhotoOpen} onClose={() => setConfirmPhotoOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: "#C4A574", color: "white", fontWeight: 700 }}>
            Confirm Photo Upload
          </DialogTitle>
          <DialogContent sx={{ pt: 3, bgcolor: "#2a2a2a", color: "#fff" }}>
            <Typography>
              Are you sure you want to upload this photo as your profile picture?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1, bgcolor: "#2a2a2a" }}>
            <Button
              onClick={() => {
                setConfirmPhotoOpen(false);
                setPendingPhoto(null);
              }}
              sx={{ color: "#8B7355", fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmPhotoUpload}
              variant="contained"
              disabled={uploading}
              sx={{
                bgcolor: "#C4A574",
                color: "#000",
                fontWeight: 600,
                "&:hover": { bgcolor: "#B39562" },
              }}
            >
              {uploading ? <CircularProgress size={24} sx={{ color: "#000" }} /> : "Upload Photo"}
            </Button>
          </DialogActions>
        </Dialog>
        </Box>
      </Box>
    </AppLayout>
  );
}